/**
 * ListActionsContextMenu — pływające menu akcji przy kursorze (PHASE 25, trzecie
 * wejście: prawy klik na wierszu/zaznaczeniu). Renderuje te same pozycje co
 * dropdown „Akcje" (`ActionMenuItems`), portalem do body na pozycji `x/y`.
 * Zamykane klikiem poza / Esc / po wykonaniu akcji.
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ListActionId } from '@saas/shared';

import type { ListActionsMenuProps } from './ListActionsMenu';
import { ActionMenuItems } from './ListActionsMenu';

export interface ListActionsContextMenuProps extends Omit<
  ListActionsMenuProps,
  'onRun' | 'testIdPrefix'
> {
  /** Pozycja kursora (clientX/clientY). */
  x: number;
  y: number;
  onRun: (actionId: ListActionId) => void;
  onClose: () => void;
  testIdPrefix?: string;
}

export function ListActionsContextMenu({
  x,
  y,
  onClose,
  onRun,
  testIdPrefix = 'list',
  ...menuProps
}: ListActionsContextMenuProps): React.ReactElement {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    // `mousedown` zamyka na klik poza; capture, by wyprzedzić re-otwarcie przez inny handler.
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={rootRef}
      role="menu"
      style={{ position: 'fixed', top: y, left: x, zIndex: 9999, minWidth: 240 }}
      className="rounded-lg border border-border-secondary bg-bg-elevated py-1 shadow-lg"
      data-testid={`${testIdPrefix}-actions-context-menu`}
    >
      <ActionMenuItems
        {...menuProps}
        testIdPrefix={`${testIdPrefix}-ctx`}
        onRun={onRun}
        onAfterRun={onClose}
      />
    </div>,
    document.body,
  );
}
